import { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useExpenseDetail, useDeleteExpense } from '@/hooks/useExpenses';
import { useGroupDetail } from '@/hooks/useGroups';
import { canModifyExpense } from '@/lib/expensePermissions';
import { useAuth } from '@/contexts/AuthContext';
import { AmountDisplay } from '@/components/common/AmountDisplay';
import { CategoryIcon } from '@/components/common/CategoryIcon';
import { UserAvatar } from '@/components/common/UserAvatar';
import { ExpenseComments } from '@/components/expenses/ExpenseComments';
import { DisputeSection } from '@/components/disputes/DisputeSection';
import { ReceiptViewer } from '@/components/expenses/ReceiptViewer';
import { LocationCard } from '@/components/expenses/LocationCard';
import { useGroupRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import { SPLIT_TYPE_LABELS } from '@sharecost/shared';
import { Loader2, ArrowLeft, Trash2, Edit } from 'lucide-react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

export function ExpenseDetailPage() {
  const { t } = useTranslation();
  const { id: expenseId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: expense, isLoading } = useExpenseDetail(expenseId);
  const deleteExpense = useDeleteExpense();
  const { data: group } = useGroupDetail(expense?.group_id);

  // Live updates for comments (and other group data) via Supabase Realtime
  useGroupRealtimeSubscription(expense?.group_id);

  // Remember the group of an expense we have shown, so that when it is deleted
  // on another device (refetch resolves to null) we can leave to the group
  // instead of rendering a dead page. A direct open of a missing expense has
  // no remembered group and keeps the "not found" state.
  const lastGroupIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (expense?.group_id) {
      lastGroupIdRef.current = expense.group_id;
    }
  }, [expense?.group_id]);
  useEffect(() => {
    if (!isLoading && expense === null && lastGroupIdRef.current) {
      navigate(`/groups/${lastGroupIdRef.current}`, { replace: true });
    }
  }, [isLoading, expense, navigate]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
      </div>
    );
  }

  if (!expense) {
    return (
      <div className="text-center py-12">
        <p className="text-text-tertiary">{t('expenses:detail.expenseNotFound')}</p>
      </div>
    );
  }

  const handleDelete = async () => {
    if (!confirm(t('expenses:alert.deleteExpenseConfirm'))) return;
    try {
      await deleteExpense.mutateAsync({ expenseId: expense.id, groupId: expense.group_id });
      navigate(`/groups/${expense.group_id}`);
    } catch {
      // Error handled by mutation
    }
  };

  const totalAmount = Number(expense.amount_minor_src);
  const categoryColor = expense.category?.color ?? '#6B7280';
  const canModify = canModifyExpense(expense, user?.id, group);

  return (
    <div className="mx-auto max-w-2xl">
      <button
        onClick={() => navigate(-1)}
        className="mb-4 flex items-center gap-1 text-sm text-text-tertiary hover:text-text-secondary"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('common:button.back')}
      </button>

      {/* Hero Header */}
      <div
        className="rounded-t-xl p-6 pb-5"
        style={{ backgroundColor: `${categoryColor}0D` }}
      >
        <div className="flex items-start gap-4">
          <CategoryIcon
            icon={expense.category?.icon}
            color={categoryColor}
            size="lg"
          />
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-text-primary truncate">{expense.description}</h1>
            <AmountDisplay
              amountMinor={totalAmount}
              currency={expense.currency_code_src}
              className="mt-1 block text-2xl font-bold text-text-primary"
            />
          </div>
        </div>

        {/* Meta row */}
        <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm text-text-secondary">
          <span className="inline-flex items-center gap-1.5">
            <UserAvatar
              name={expense.paid_by_user?.display_name}
              avatarUrl={expense.paid_by_user?.avatar_url}
              size="xs"
            />
            {t('expenses:detail.paidBy')} {expense.paid_by_user?.display_name ?? t('web:common.unknownUser')}
          </span>
          <span className="text-text-muted">·</span>
          <span>{format(new Date(expense.expense_date), 'MMM d, yyyy')}</span>
          {expense.category && (
            <>
              <span className="text-text-muted">·</span>
              <span
                className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                style={{ backgroundColor: `${categoryColor}1A`, color: categoryColor }}
              >
                {expense.category.name}
              </span>
            </>
          )}
          {expense.split_type && (
            <>
              <span className="text-text-muted">·</span>
              <span className="text-text-tertiary">
                {SPLIT_TYPE_LABELS[expense.split_type] ?? expense.split_type} split
              </span>
            </>
          )}
        </div>
      </div>

      {/* Body card */}
      <div className="rounded-b-xl bg-surface-primary p-6 shadow-sm ring-1 ring-border-primary space-y-5">
        {/* Info cards: location, receipt */}
        {(expense.location_name || expense.latitude || expense.receipt_url) && (
          <div className="space-y-3">
            <LocationCard
              locationName={expense.location_name}
              latitude={expense.latitude}
              longitude={expense.longitude}
            />
            {expense.receipt_url && (
              <ReceiptViewer receiptUrl={expense.receipt_url} />
            )}
          </div>
        )}

        {/* Split breakdown */}
        {expense.splits && expense.splits.length > 0 && (
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-text-quaternary mb-3">
              {t('expenses:detail.splitBreakdown')}
            </h3>
            <div className="space-y-3">
              {expense.splits.map(split => {
                const splitAmount = Number(split.amount_minor);
                const percentage = totalAmount > 0
                  ? Math.round((splitAmount / totalAmount) * 100)
                  : 0;
                const isPayer = split.user_id === expense.paid_by;

                return (
                  <div key={split.id}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <UserAvatar
                          name={split.user?.display_name}
                          avatarUrl={split.user?.avatar_url}
                          size="sm"
                        />
                        <span className="text-sm font-medium text-text-primary truncate">
                          {split.user?.display_name ?? t('web:common.unknownUser')}
                        </span>
                        {isPayer && (
                          <span className="shrink-0 rounded-full bg-green-100 dark:bg-green-900/30 px-2 py-0.5 text-[10px] font-semibold text-green-700 dark:text-green-400">
                            paid
                          </span>
                        )}
                      </div>
                      <AmountDisplay
                        amountMinor={splitAmount}
                        currency={expense.currency_code_src}
                        className="text-sm font-semibold text-text-primary shrink-0 ml-3"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 flex-1 rounded-full bg-surface-tertiary overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${percentage}%`,
                            backgroundColor: categoryColor,
                            opacity: 0.6,
                          }}
                        />
                      </div>
                      <span className="text-[11px] text-text-quaternary tabular-nums w-8 text-right">
                        {percentage}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Disputes */}
        <DisputeSection
          expenseId={expense.id}
          groupId={expense.group_id}
          paidBy={expense.paid_by}
          expenseDescription={expense.description}
          isUserParticipant={
            !!user && expense.paid_by !== user.id &&
            !!expense.splits?.some(s => s.user_id === user.id)
          }
          onEditExpense={() => navigate(`/expenses/${expense.id}/edit`)}
        />

        {/* Comments */}
        <ExpenseComments expenseId={expense.id} groupId={expense.group_id} />

        {/* Actions */}
        {canModify && (
          <div className="flex items-center gap-3 pt-2 border-t border-border-tertiary">
            <button
              onClick={() => navigate(`/expenses/${expense.id}/edit`)}
              className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 transition-colors"
            >
              <Edit className="h-4 w-4" />
              {t('common:button.edit')}
            </button>
            <button
              onClick={handleDelete}
              disabled={deleteExpense.isPending}
              className="flex items-center gap-2 text-sm text-text-quaternary hover:text-red-500 dark:hover:text-red-400 transition-colors disabled:opacity-50"
            >
              {deleteExpense.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              {t('common:button.delete')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
